"use client";

import { useMemo, useRef, useState } from "react";

type DressingProduct = {
  lab: string;
  name: string;
  variants?: {
    size: string;
    ean: string;
  }[];
};

type DressingFamily = {
  id: string;
  group: string;
  label: string;
  icon: string;
  description: string;
  absorption: number;
  adhesion: "Adhésif" | "Non adhésif" | "Interface" | "Fixation";
  shapes?: string[];
  stages?: string[];
  advice: string;
  products: DressingProduct[];
};

const groups = [
  "Toutes",
  "Hydrocellulaires",
  "Superabsorbants",
  "Hydrofibres",
  "Hydrocolloïdes",
  "Interfaces",
  "Films",
  "Compresses",
  "Fixation",
] as const;

const woundStages = [
  { id: "necrotique", label: "Nécrotique", color: "#16181b" },
  { id: "fibrineuse", label: "Fibrineuse", color: "#f3c51d" },
  { id: "granulation", label: "Granulation", color: "#e94d43" },
  { id: "epithelialisation", label: "Épithélialisation", color: "#e789a3" },
] as const;

const families: DressingFamily[] = [
  {
    id: "foam-border-lite",
    group: "Hydrocellulaires",
    label: "Hydrocellulaire bordé fin",
    icon: "▭",
    description: "Plaies superficielles, peau fragile, exsudat faible à modéré.",
    absorption: 1,
    adhesion: "Adhésif",
    shapes: ["Carré"],
    stages: ["granulation", "epithelialisation"],
    advice: "Conserver un bord siliconé souple et une absorption légère. Ne pas remplacer par une mousse très absorbante sans réévaluation.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofoam Border Lite",
        variants: [
          { size: "XS · 5 × 5 cm · compresse 2,5 × 2,5 cm", ean: "3615370001174" },
          { size: "S · 7,5 × 8,5 cm · compresse 4,5 × 5 cm", ean: "3615370002539" },
          { size: "S · 6 × 12 cm · compresse 3 × 8 cm", ean: "3615370001150" },
          { size: "M · 9 × 15 cm · compresse 5 × 10,5 cm", ean: "3615370001167" },
          { size: "L · 10 × 20 cm · compresse 5 × 15 cm", ean: "3615370001143" },
        ],
      },
      { lab: "Lohmann & Rauscher", name: "Suprasorb P Sensitive Border Lite" },
      { lab: "Mölnlycke", name: "Mepilex Border Flex EM" },
      { lab: "Smith & Nephew", name: "Allevyn Gentle Border Lite" },
      { lab: "Urgo", name: "Urgotul Lite Border" },
      { lab: "Convatec", name: "Aquacel Foam Lite" },
      { lab: "Coloplast", name: "Biatain Silicone Lite" },
      { lab: "KCI Medical", name: "Tielle Lite" },
    ],
  },
  {
    id: "foam-border",
    group: "Hydrocellulaires",
    label: "Hydrocellulaire siliconé avec bord",
    icon: "▣",
    description: "Plaies superficielles à profondes, exsudat modéré à important.",
    absorption: 3,
    adhesion: "Adhésif",
    shapes: ["Carré"],
    stages: ["fibrineuse", "granulation", "epithelialisation"],
    advice: "Comparer la taille totale et la taille de compresse. Garder un bord siliconé si la peau périlésionnelle est fragile.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofoam Border",
        variants: [
          { size: "S · 7,5 × 8,5 cm · compresse 4,5 × 5 cm", ean: "3615370002287" },
          { size: "S · 7,5 × 8,5 cm · compresse 4,5 × 5 cm · boîte de 16", ean: "3615370001181" },
          { size: "S · 10 × 10 cm · compresse 6,2 × 6,2 cm", ean: "3615370004540" },
          { size: "S · 12,5 × 12,5 cm · compresse 8,5 × 8,5 cm", ean: "3615370002553" },
          { size: "M · 14 × 15 cm · compresse 10 × 10,5 cm", ean: "3615370001198" },
          { size: "L · 17,5 × 17,5 cm · compresse 12,5 × 12,5 cm", ean: "3615370001204" },
          { size: "L · 17,5 × 23 cm · compresse 12,5 × 17,5 cm", ean: "3615370004557" },
          { size: "XL · 10 × 30 cm · compresse 5 × 24 cm", ean: "3615370002652" },
        ],
      },
      { lab: "Hartmann", name: "HydroTac Comfort" },
      { lab: "Lohmann & Rauscher", name: "Suprasorb P Sensitive Border" },
      { lab: "Mölnlycke", name: "Mepilex Border Flex" },
      { lab: "3M", name: "Tegaderm Foam Adhesive" },
      { lab: "Smith & Nephew", name: "Allevyn Gentle Border" },
      { lab: "Urgo", name: "Urgotul Border" },
      { lab: "BSN", name: "Cutimed Siltec B" },
      { lab: "Convatec", name: "Aquacel Foam" },
      { lab: "Coloplast", name: "Biatain Silicone" },
      { lab: "KCI Medical", name: "Tielle Silicone Border" },
    ],
  },
  {
    id: "foam-border-oval",
    group: "Hydrocellulaires",
    label: "Hydrocellulaire bordé ovale / multisite",
    icon: "⬭",
    description: "Zones anatomiques difficiles et plaies exsudatives.",
    absorption: 3,
    adhesion: "Adhésif",
    shapes: ["Ovale", "Multisite"],
    stages: ["fibrineuse", "granulation", "epithelialisation"],
    advice: "L’équivalence porte sur la forme anatomique autant que sur la capacité d’absorption.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofoam Border format ovale",
        variants: [
          { size: "S · 8 × 10,3 cm · compresse 3,5 × 5,5 cm", ean: "3615370005073" },
          { size: "M · 10,2 × 12,5 cm · compresse 6 × 8,5 cm", ean: "3615370005066" },
          { size: "L · 15 × 18 cm · compresse 9 × 12 cm", ean: "3615370004809" },
          { size: "XL · 18 × 22 cm · compresse 11,5 × 15,5 cm", ean: "3615370004816" },
        ],
      },
      { lab: "Lohmann & Rauscher", name: "Suprasorb P Sensitive Multisite Border Lite" },
      { lab: "Smith & Nephew", name: "Allevyn Gentle Border Lite Oval" },
      { lab: "Mölnlycke", name: "Mepilex Border Flex Oval" },
      { lab: "KCI Medical", name: "Tielle Essential Surgical" },
    ],
  },
  {
    id: "foam-sacrum",
    group: "Hydrocellulaires",
    label: "Hydrocellulaire sacrum",
    icon: "♢",
    description: "Escarre sacrée ou plaie de la zone sacrée.",
    absorption: 3,
    adhesion: "Adhésif",
    shapes: ["Sacrum"],
    stages: ["fibrineuse", "granulation", "epithelialisation"],
    advice: "Ne retenir que les formes sacrées. Vérifier le sens de pose et les dimensions de la zone absorbante.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofoam Border sacrum",
        variants: [
          { size: "L · 17,5 × 17 cm · compresse 12,5 × 12 cm", ean: "3615370003055" },
          { size: "XL · 23 × 23 cm · compresse 17 × 16,5 cm", ean: "3615370003062" },
        ],
      },
      { lab: "Hartmann", name: "HydroTac Sacral" },
      { lab: "Lohmann & Rauscher", name: "Suprasorb P Sensitive Sacrum" },
      { lab: "Mölnlycke", name: "Mepilex Border Sacrum" },
      { lab: "Smith & Nephew", name: "Allevyn Sacrum" },
      { lab: "Convatec", name: "Aquacel Foam Sacrum" },
      { lab: "Coloplast", name: "Biatain Silicone Sacrum" },
      { lab: "KCI Medical", name: "Tielle Pansement Sacrum" },
    ],
  },
  {
    id: "foam-heel",
    group: "Hydrocellulaires",
    label: "Hydrocellulaire talon",
    icon: "◒",
    description: "Plaie ou prévention de l’escarre du talon.",
    absorption: 3,
    adhesion: "Adhésif",
    shapes: ["Talon"],
    stages: ["fibrineuse", "granulation", "epithelialisation"],
    advice: "La tenue anatomique au talon est déterminante : ne pas basculer vers un simple carré.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofoam Border talon",
        variants: [
          { size: "XL · 23,5 × 25 cm · compresse 19 × 17,5 cm", ean: "3615370004243" },
        ],
      },
      { lab: "Hartmann", name: "HydroTac Concave" },
      { lab: "Lohmann & Rauscher", name: "Suprasorb P Sensitive Talon" },
      { lab: "Mölnlycke", name: "Mepilex Border Heel" },
      { lab: "3M", name: "Tegaderm Silicone Foam Border Talon" },
      { lab: "Smith & Nephew", name: "Allevyn Heel" },
      { lab: "Urgo", name: "Urgotul Absorb Talon" },
      { lab: "Convatec", name: "Aquacel Foam Adhesive Talon" },
      { lab: "Coloplast", name: "Biatain Silicone Talon" },
      { lab: "KCI Medical", name: "Tielle Talon" },
    ],
  },
  {
    id: "foam-no-border",
    group: "Hydrocellulaires",
    label: "Hydrocellulaire non adhésif",
    icon: "□",
    description: "Plaies exsudatives lorsque la fixation est assurée séparément.",
    absorption: 3,
    adhesion: "Non adhésif",
    shapes: ["Carré"],
    stages: ["fibrineuse", "granulation", "epithelialisation"],
    advice: "Prévoir une fixation secondaire. Vérifier que le produit demandé est bien sans bord adhésif.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofoam SB",
        variants: [
          { size: "S · 12,5 × 12,5 cm", ean: "3615370001990" },
          { size: "M · 14 × 15 cm", ean: "3615370002003" },
          { size: "L · 20 × 20 cm", ean: "3615370002010" },
        ],
      },
      { lab: "Hartmann", name: "HydroTac" },
      { lab: "Lohmann & Rauscher", name: "Suprasorb P non adhésif" },
      { lab: "Mölnlycke", name: "Mepilex XT" },
      { lab: "3M", name: "Tegaderm Foam Non-Adhesive" },
      { lab: "Smith & Nephew", name: "Allevyn Gentle" },
      { lab: "Urgo", name: "Urgotul Absorb" },
      { lab: "Convatec", name: "Aquacel Foam non adhésif" },
      { lab: "Coloplast", name: "Biatain Non-Adhesive" },
      { lab: "KCI Medical", name: "Tielle Non Adhésif" },
    ],
  },
  {
    id: "superabsorbent",
    group: "Superabsorbants",
    label: "Pansement superabsorbant",
    icon: "💧",
    description: "Plaies très exsudatives, protection et gestion de forts volumes.",
    absorption: 4,
    adhesion: "Non adhésif",
    shapes: ["Carré"],
    stages: ["fibrineuse", "granulation"],
    advice: "Conserver la catégorie superabsorbante et vérifier la compatibilité avec la compression si elle est prévue.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absosorb",
        variants: [
          { size: "10 × 10 cm", ean: "3615370000276" },
          { size: "10 × 20 cm", ean: "3615370000283" },
          { size: "15 × 20 cm", ean: "3615370000290" },
          { size: "20 × 25 cm", ean: "3615370000306" },
          { size: "20 × 40 cm", ean: "3615370000313" },
        ],
      },
      { lab: "Hartmann", name: "RespoSorb" },
      { lab: "Lohmann & Rauscher", name: "Vliwasorb Pro" },
      { lab: "Mölnlycke", name: "Mextra Superabsorbant" },
      { lab: "3M", name: "Tegaderm Superabsorber" },
      { lab: "BSN", name: "Cutimed Sorbion" },
      { lab: "Convatec", name: "Convamax" },
      { lab: "KCI Medical", name: "KerraMax Care" },
      { lab: "Autres", name: "DryMax Extra" },
    ],
  },
  {
    id: "hydrofiber",
    group: "Hydrofibres",
    label: "Hydrofibre renforcée",
    icon: "≈",
    description: "Plaies profondes ou cavitaires, exsudat important.",
    absorption: 4,
    adhesion: "Non adhésif",
    shapes: ["Carré", "Mèche"],
    stages: ["fibrineuse", "granulation"],
    advice: "Distinguer plaque et mèche. Prévoir un pansement secondaire selon la situation.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofibre",
        variants: [
          { size: "S · 5 × 10 cm", ean: "3615370003543" },
          { size: "M · 12,5 × 12,5 cm", ean: "3615370004939" },
          { size: "L · 13,5 × 15 cm", ean: "3615370003567" },
          { size: "XL · 20 × 24 cm", ean: "3615370003574" },
          { size: "Mèche · 2,5 × 40 cm", ean: "3615370004922" },
        ],
      },
      { lab: "Mölnlycke", name: "Exufiber" },
      { lab: "Tetra", name: "Biosorb" },
      { lab: "BSN", name: "Cutimed Sorbion S" },
      { lab: "Convatec", name: "Aquacel Extra / Aquacel Mèche" },
      { lab: "Coloplast", name: "Biatain Fiber" },
      { lab: "KCI Medical", name: "Kerracel" },
    ],
  },
  {
    id: "hydrocolloid-thin",
    group: "Hydrocolloïdes",
    label: "Hydrocolloïde extra-mince",
    icon: "◇",
    description: "Plaies superficielles peu exsudatives, protection et épithélialisation.",
    absorption: 1,
    adhesion: "Adhésif",
    shapes: ["Carré"],
    stages: ["granulation", "epithelialisation"],
    advice: "Réserver aux plaies peu exsudatives. Ne pas confondre avec un hydrocellulaire.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absocol",
        variants: [
          { size: "XS · 5 × 7 cm", ean: "3615370005110" },
          { size: "S · 5 × 10 cm", ean: "3615370005127" },
          { size: "M · 10 × 10 cm", ean: "3615370005134" },
          { size: "M · 10 × 15 cm", ean: "3615370005141" },
          { size: "L · 12,5 × 12,5 cm", ean: "3615370005158" },
          { size: "XL · 20 × 20 cm", ean: "3615370005165" },
        ],
      },
      { lab: "Hartmann", name: "Hydrocoll Thin" },
      { lab: "Lohmann & Rauscher", name: "Suprasorb H fin" },
      { lab: "Convatec", name: "DuoDERM Extra Mince" },
      { lab: "Coloplast", name: "Comfeel Plus Transparent" },
    ],
  },
  {
    id: "fatty-interface",
    group: "Interfaces",
    label: "Interface grasse stérile",
    icon: "▦",
    description: "Brûlures, dermabrasions, plaies superficielles et bourgeonnantes.",
    absorption: 0,
    adhesion: "Interface",
    shapes: ["Carré"],
    stages: ["granulation", "epithelialisation"],
    advice: "Interface non absorbante : toujours vérifier le besoin d’un pansement secondaire.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absotulle Gras",
        variants: [
          { size: "10 × 10 cm", ean: "3401096715811" },
        ],
      },
      { lab: "Hartmann", name: "Grassolind" },
      { lab: "Lohmann & Rauscher", name: "Lomatuell H" },
      { lab: "Tetra", name: "Tetratul" },
      { lab: "3M", name: "Mefra Tulle" },
      { lab: "Smith & Nephew", name: "Jelonet" },
      { lab: "Urgo", name: "Urgotul" },
      { lab: "BSN", name: "Cuticell Classic" },
      { lab: "Coloplast", name: "Physiotulle" },
      { lab: "KCI Medical", name: "Adaptic" },
      { lab: "Autres", name: "Tullegras" },
    ],
  },
  {
    id: "adhesive-dressing",
    group: "Compresses",
    label: "Pansement adhésif stérile",
    icon: "▤",
    description: "Plaies post-opératoires superficielles, propres et peu exsudatives.",
    absorption: 1,
    adhesion: "Adhésif",
    shapes: ["Rectangulaire"],
    stages: ["granulation", "epithelialisation"],
    advice: "Comparer la taille de la compresse centrale, pas seulement la taille extérieure.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absoderm",
        variants: [
          { size: "5 × 7 cm · compresse 2,5 × 4 cm", ean: "3401060213343" },
          { size: "8 × 10 cm · compresse 4,5 × 6 cm", ean: "3401060122591" },
          { size: "10 × 15 cm · compresse 5 × 10 cm", ean: "3401060122607" },
          { size: "10 × 25 cm · compresse 5 × 20 cm", ean: "3401060122614" },
        ],
      },
      { lab: "Hartmann", name: "Cosmopor E" },
      { lab: "Lohmann & Rauscher", name: "Curapor" },
      { lab: "Mölnlycke", name: "Mepore" },
      { lab: "Tetra", name: "Tetraplaie" },
      { lab: "3M", name: "Medipore + Pad" },
      { lab: "Smith & Nephew", name: "Cicaplaie" },
      { lab: "Urgo", name: "Urgostérile" },
      { lab: "BSN", name: "Leukomed" },
    ],
  },
  {
    id: "transparent-film",
    group: "Films",
    label: "Film transparent stérile",
    icon: "▱",
    description: "Protection, fixation de cathéter et plaies superficielles non exsudatives.",
    absorption: 0,
    adhesion: "Adhésif",
    shapes: ["Rectangulaire"],
    stages: ["epithelialisation"],
    advice: "Un film seul n’a pas de compresse absorbante. Distinguer rouleau, plaque stérile et version avec compresse.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofilm",
        variants: [
          { size: "10 × 12 cm", ean: "3401060122645" },
          { size: "15 × 20 cm", ean: "3401060122652" },
        ],
      },
      { lab: "Hartmann", name: "Hydrofilm" },
      { lab: "Lohmann & Rauscher", name: "Suprasorb F" },
      { lab: "Mölnlycke", name: "Mepore Film" },
      { lab: "3M", name: "Tegaderm" },
      { lab: "Smith & Nephew", name: "Opsite Flexifix" },
      { lab: "Urgo", name: "Optiskin Film" },
      { lab: "BSN", name: "Leukomed T" },
    ],
  },
  {
    id: "transparent-film-pad",
    group: "Films",
    label: "Film transparent avec compresse",
    icon: "▥",
    description: "Plaies post-opératoires peu exsudatives, visibles sous le film.",
    absorption: 1,
    adhesion: "Adhésif",
    shapes: ["Rectangulaire"],
    stages: ["granulation", "epithelialisation"],
    advice: "Vérifier la présence et la taille de la compresse centrale.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofilm + Pad",
        variants: [
          { size: "5 × 7 cm · compresse 2,5 × 4 cm", ean: "3615371700014" },
          { size: "8 × 10 cm · compresse 4,5 × 6 cm", ean: "3615371700021" },
          { size: "10 × 15 cm · compresse 5 × 10 cm", ean: "3615371700038" },
        ],
      },
      { lab: "Hartmann", name: "Hydrofilm Plus" },
      { lab: "Lohmann & Rauscher", name: "Curapor Transparent" },
      { lab: "Mölnlycke", name: "Mepore Film + Pad" },
      { lab: "Tetra", name: "Tetraplaie Film" },
      { lab: "3M", name: "Tegaderm + Pad" },
      { lab: "Smith & Nephew", name: "Opsite Post-Op" },
      { lab: "Urgo", name: "Optiskin" },
      { lab: "BSN", name: "Leukomed T Plus" },
    ],
  },
  {
    id: "sterile-gauze",
    group: "Compresses",
    label: "Compresse gaze stérile",
    icon: "▦",
    description: "Nettoyage, protection ou absorption selon le soin.",
    absorption: 1,
    adhesion: "Non adhésif",
    shapes: ["Carré"],
    advice: "Comparer le nombre de plis, le nombre de compresses et la dimension dépliée.",
    products: [
      { lab: "Hartmann", name: "Sterilux" },
      { lab: "Lohmann & Rauscher", name: "Gazin" },
      { lab: "Mölnlycke", name: "Gauze" },
      { lab: "Tetra", name: "Tetra Compresse" },
      { lab: "3M", name: "Mefra" },
      { lab: "Smith & Nephew", name: "Melolin" },
      { lab: "Urgo", name: "Urgo Compresse" },
    ],
  },
  {
    id: "nonwoven-compress",
    group: "Compresses",
    label: "Compresse non tissée stérile",
    icon: "▧",
    description: "Soin courant, nettoyage et recouvrement de plaies.",
    absorption: 1,
    adhesion: "Non adhésif",
    shapes: ["Carré"],
    advice: "Comparer grammage, nombre de plis, taille et quantité par boîte.",
    products: [
      { lab: "Hartmann", name: "Medicomp" },
      { lab: "Lohmann & Rauscher", name: "Vliwasoft" },
      { lab: "Mölnlycke", name: "Mesoft" },
      { lab: "Tetra", name: "Tetra non tissée" },
      { lab: "3M", name: "Mefra non tissée" },
      { lab: "Urgo", name: "Urgo non tissée" },
    ],
  },
  {
    id: "microporous-tape",
    group: "Fixation",
    label: "Sparadrap microporeux",
    icon: "═",
    description: "Fixation légère sur peau normale à fragile.",
    absorption: 0,
    adhesion: "Fixation",
    shapes: ["Rouleau"],
    advice: "Vérifier largeur, longueur, support et tolérance cutanée.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absopore",
        variants: [
          { size: "5 m × 2,5 cm", ean: "3401079981318" },
          { size: "9,14 m × 2,5 cm", ean: "3401079981257" },
        ],
      },
      { lab: "Hartmann", name: "Omnipor" },
      { lab: "Lohmann & Rauscher", name: "Velpeau Pore" },
      { lab: "Tetra", name: "Tetrapore" },
      { lab: "3M", name: "Micropore" },
      { lab: "Urgo", name: "Urgopore" },
      { lab: "BSN", name: "AlbuPore" },
    ],
  },
  {
    id: "adhesive-fixation",
    group: "Fixation",
    label: "Bande adhésive extensible",
    icon: "〰",
    description: "Maintien de pansements et fixation sur zones mobiles.",
    absorption: 0,
    adhesion: "Fixation",
    shapes: ["Rouleau"],
    advice: "Comparer la largeur, l’élasticité du support et la tolérance de l’adhésif.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absofix",
        variants: [
          { size: "2,5 m × 10 cm", ean: "3401072091281" },
          { size: "5 m × 5 cm", ean: "3401072091113" },
          { size: "10 m × 5 cm", ean: "3401098218563" },
          { size: "10 m × 10 cm", ean: "3401072091342" },
        ],
      },
      { lab: "Hartmann", name: "Omnifix Elastic" },
      { lab: "Lohmann & Rauscher", name: "Curafix H" },
      { lab: "Mölnlycke", name: "Mefix" },
      { lab: "Tetra", name: "Tetrafixe" },
      { lab: "3M", name: "Medipore Liner" },
      { lab: "Urgo", name: "Urgoderm" },
      { lab: "BSN", name: "Fixomull Stretch" },
    ],
  },
  {
    id: "cohesive-bandage",
    group: "Fixation",
    label: "Bande cohésive extensible",
    icon: "≋",
    description: "Fixation sans adhérer directement à la peau.",
    absorption: 0,
    adhesion: "Fixation",
    shapes: ["Rouleau"],
    advice: "Une bande cohésive adhère sur elle-même. Ne pas la confondre avec une bande adhésive ou de compression.",
    products: [
      {
        lab: "Marque Verte",
        name: "Absohaft",
        variants: [
          { size: "4 m × 6 cm", ean: "3615370001464" },
          { size: "4 m × 8 cm", ean: "3615370001471" },
          { size: "4 m × 10 cm", ean: "3615370001488" },
        ],
      },
      { lab: "Hartmann", name: "Peha-haft" },
      { lab: "Lohmann & Rauscher", name: "Mollelast Haft" },
      { lab: "Tetra", name: "Tetra Cohésive" },
      { lab: "3M", name: "Coban" },
      { lab: "Urgo", name: "Nylexfix" },
      { lab: "BSN", name: "Elastomull Haft" },
    ],
  },
];

const normalize = (value: string) =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr");

type SupplyProfile = {
  priority: number;
  label: string;
  channel: string;
  className: string;
};

const supplyProfile = (lab: string): SupplyProfile => {
  const normalizedLab = normalize(lab);
  if (normalizedLab.includes("soineo")) {
    return { priority: 0, label: "Priorité +++", channel: "Direct Soineo", className: "soineo" };
  }
  if (normalizedLab.includes("marque verte")) {
    return { priority: 1, label: "Dépannage facile", channel: "Fil de l’eau CERP", className: "marque-verte" };
  }
  if (normalizedLab.includes("lohmann")) {
    return { priority: 2, label: "Marque préférée", channel: "Direct Lohmann", className: "lohmann" };
  }
  if (normalizedLab.includes("hartmann")) {
    return { priority: 3, label: "Produit technique", channel: "CERP · remise légère", className: "hartmann" };
  }
  return { priority: 9, label: "Autre équivalent", channel: "Circuit habituel", className: "standard" };
};

const productText = (product: DressingProduct) =>
  `${product.lab} ${product.name} ${product.variants?.map((variant) => `${variant.size} ${variant.ean}`).join(" ") ?? ""}`;

const familyText = (family: DressingFamily) =>
  normalize(`${family.label} ${family.group} ${family.description} ${family.advice} ${family.products.map(productText).join(" ")}`);

function AbsorptionScale({ level }: { level: number }) {
  return (
    <span className="dressing-drops" aria-label={`Absorption ${level} sur 4`}>
      {[1, 2, 3, 4].map((item) => <i key={item} className={item <= level ? "filled" : ""}>●</i>)}
    </span>
  );
}

function ProductVariants({ product, defaultOpen = false }: { product: DressingProduct; defaultOpen?: boolean }) {
  if (!product.variants?.length) return null;
  return (
    <details className="dressing-product-variants" open={defaultOpen || undefined}>
      <summary>
        <span>▦ {product.variants.length} taille{product.variants.length > 1 ? "s" : ""}</span>
        <b>Tailles & EAN</b>
      </summary>
      <div>
        {product.variants.map((variant) => (
          <p key={`${product.name}-${variant.ean}`}>
            <span>{variant.size}</span>
            <code>{variant.ean}</code>
          </p>
        ))}
      </div>
    </details>
  );
}

function DressingEquivalenceLegacy({ onBack }: { onBack: () => void }) {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<(typeof groups)[number]>("Toutes");
  const [stage, setStage] = useState("");
  const [absorption, setAbsorption] = useState(0);
  const [adhesion, setAdhesion] = useState("");
  const [selectedId, setSelectedId] = useState(families[0].id);
  const [notice, setNotice] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const needle = normalize(query.trim());
    return families
      .filter((family) => group === "Toutes" || family.group === group)
      .filter((family) => !stage || family.stages?.includes(stage))
      .filter((family) => !absorption || family.absorption === absorption)
      .filter((family) => !adhesion || family.adhesion === adhesion)
      .filter((family) => !needle || familyText(family).includes(needle))
      .sort((a, b) => {
        if (!needle) return a.label.localeCompare(b.label, "fr");
        const exactA = a.products.some((product) => normalize(product.name) === needle) ? 0 : normalize(a.label).startsWith(needle) ? 1 : 2;
        const exactB = b.products.some((product) => normalize(product.name) === needle) ? 0 : normalize(b.label).startsWith(needle) ? 1 : 2;
        return exactA - exactB || a.label.localeCompare(b.label, "fr");
      });
  }, [absorption, adhesion, group, query, stage]);

  const selected = results.find((family) => family.id === selectedId) ?? results[0];
  const productCount = families.reduce((total, family) => total + family.products.length, 0);
  const reset = () => {
    setQuery("");
    setGroup("Toutes");
    setStage("");
    setAbsorption(0);
    setAdhesion("");
    setSelectedId(families[0].id);
    window.setTimeout(() => searchRef.current?.focus(), 0);
  };

  const copyFamily = async () => {
    if (!selected) return;
    const value = `${selected.label}\n${selected.products.map((product) => `${product.lab} : ${product.name}`).join("\n")}\nÀ vérifier : dimensions, indication, remboursement et disponibilité.`;
    try {
      await navigator.clipboard.writeText(value);
      setNotice("Liste copiée");
    } catch {
      setNotice("Copie non disponible sur ce poste");
    }
    window.setTimeout(() => setNotice(""), 2200);
  };

  return (
    <main className="dressing-module">
      <header className="module-header dressing-module-header">
        <button type="button" className="back-button" onClick={onBack}>← Accueil</button>
        <div className="module-brand"><span className="brand-mark">sp</span><div><strong>saint-laurent</strong><small>PHARMACIES</small></div></div>
        <div className="module-header-actions">
          <a className="module-tools-shortcut" href="/?tools=1" title="Ouvrir les applications"><span>🧰</span><b>Applications</b></a>
          <span className="module-status"><i />Référentiel comptoir</span>
        </div>
      </header>

      <section className="dressing-hero">
        <div>
          <p className="dressing-kicker">🩹 AIDE AU CHOIX</p>
          <h1>Équivalences pansements</h1>
          <p>Retrouver une alternative par produit, laboratoire ou caractéristique de la plaie — sans parcourir un tableau illisible.</p>
        </div>
        <div className="dressing-hero-stats">
          <span><b>{families.length}</b> familles</span>
          <span><b>{productCount}</b> références</span>
          <span><b>1</b> réponse claire</span>
        </div>
      </section>

      <section className="dressing-search-card">
        <div className="dressing-search-heading">
          <div>
            <p>Je connais le produit</p>
            <h2>Quel pansement cherchez-vous ?</h2>
          </div>
          {(query || group !== "Toutes" || stage || absorption || adhesion) && <button type="button" onClick={reset}>Tout effacer</button>}
        </div>
        <label className="dressing-search">
          <span>⌕</span>
          <input
            ref={searchRef}
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ex. Mepilex Border, Aquacel, Urgotul, Tegaderm…"
          />
          {query && <button type="button" onClick={() => setQuery("")}>×</button>}
        </label>
        <div className="dressing-group-scroll">
          {groups.map((item) => (
            <button type="button" key={item} className={group === item ? "active" : ""} onClick={() => setGroup(item)}>
              {item}
            </button>
          ))}
        </div>
      </section>

      <section className="dressing-guided-card">
        <div className="dressing-guided-title">
          <span>✦</span>
          <div><p>Je pars de la plaie</p><h2>Affiner la famille recherchée</h2></div>
        </div>
        <div className="dressing-guided-grid">
          <fieldset>
            <legend>Stade de cicatrisation</legend>
            <div className="dressing-stage-buttons">
              {woundStages.map((item) => (
                <button type="button" key={item.id} className={stage === item.id ? "active" : ""} onClick={() => setStage(stage === item.id ? "" : item.id)}>
                  <i style={{ backgroundColor: item.color }} />{item.label}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Niveau d’exsudat</legend>
            <div className="dressing-exudate-buttons">
              {[1, 2, 3, 4].map((level) => (
                <button type="button" key={level} className={absorption === level ? "active" : ""} onClick={() => setAbsorption(absorption === level ? 0 : level)}>
                  <span>{"💧".repeat(level)}</span><small>{["Faible", "Modéré", "Important", "Très important"][level - 1]}</small>
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Tenue sur la peau</legend>
            <div className="dressing-adhesion-buttons">
              {["Adhésif", "Non adhésif", "Interface", "Fixation"].map((item) => (
                <button type="button" key={item} className={adhesion === item ? "active" : ""} onClick={() => setAdhesion(adhesion === item ? "" : item)}>{item}</button>
              ))}
            </div>
          </fieldset>
        </div>
      </section>

      <section className="dressing-results-layout">
        <div className="dressing-results-list">
          <div className="dressing-results-head">
            <div><p>Résultats</p><h2>{results.length} famille{results.length > 1 ? "s" : ""} compatible{results.length > 1 ? "s" : ""}</h2></div>
            <span>Choisissez une famille pour voir toutes les marques</span>
          </div>
          {results.length ? results.map((family) => (
            <button type="button" key={family.id} className={`dressing-family-row ${selected?.id === family.id ? "active" : ""}`} onClick={() => setSelectedId(family.id)}>
              <span className="dressing-family-icon">{family.icon}</span>
              <span className="dressing-family-copy">
                <small>{family.group}</small>
                <strong>{family.label}</strong>
                <em>{family.description}</em>
              </span>
              <span className="dressing-family-meta">
                <AbsorptionScale level={family.absorption} />
                <small>{family.products.length} marques</small>
              </span>
              <b>›</b>
            </button>
          )) : (
            <div className="dressing-empty">
              <span>⌕</span>
              <h3>Aucune correspondance exacte</h3>
              <p>Retirez un filtre ou vérifiez l’orthographe du produit.</p>
              <button type="button" onClick={reset}>Revenir à toutes les familles</button>
            </div>
          )}
        </div>

        <aside className="dressing-detail">
          {selected ? (
            <>
              <div className="dressing-detail-head">
                <span>{selected.icon}</span>
                <div><small>{selected.group}</small><h2>{selected.label}</h2><p>{selected.description}</p></div>
              </div>
              <div className="dressing-checkpoints">
                <span><small>Absorption</small><AbsorptionScale level={selected.absorption} /></span>
                <span><small>Tenue</small><b>{selected.adhesion}</b></span>
                <span><small>Forme</small><b>{selected.shapes?.join(" · ")}</b></span>
              </div>
              <div className="dressing-equivalents-title">
                <h3>Équivalents par laboratoire</h3>
                <button type="button" onClick={copyFamily}>⧉ Copier</button>
              </div>
              <div className="dressing-equivalents">
                {selected.products.map((product) => (
                  <article key={`${selected.id}-${product.lab}-${product.name}`}>
                    <small>{product.lab}</small>
                    <strong>{product.name}</strong>
                  </article>
                ))}
              </div>
              <div className="dressing-advice">
                <span>✓</span><div><b>Point de vigilance</b><p>{selected.advice}</p></div>
              </div>
              <p className="dressing-disclaimer">Aide à l’identification d’une équivalence fonctionnelle. Vérifier avant délivrance l’indication, la dimension, la forme, le remboursement et les données fabricant.</p>
            </>
          ) : <div className="dressing-detail-placeholder">Sélectionnez une famille.</div>}
        </aside>
      </section>
      {notice && <div className="dressing-toast" role="status">{notice}</div>}
    </main>
  );
}

export function DressingEquivalenceModule({ onBack }: { onBack: () => void }) {
  const [query, setQuery] = useState("");
  const [selectedProductKey, setSelectedProductKey] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const [guideGroup, setGuideGroup] = useState<(typeof groups)[number]>("Toutes");
  const [notice, setNotice] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const productMatches = useMemo(() => {
    const needle = normalize(query.trim());
    if (needle.length < 2) return [];
    return families
      .flatMap((family) => family.products.map((product) => ({ family, product, key: `${family.id}:${product.lab}:${product.name}` })))
      .filter(({ family, product }) => normalize(`${productText(product)} ${family.label}`).includes(needle))
      .sort((a, b) => {
        const aName = normalize(a.product.name);
        const bName = normalize(b.product.name);
        const rankA = aName === needle ? 0 : aName.startsWith(needle) ? 1 : 2;
        const rankB = bName === needle ? 0 : bName.startsWith(needle) ? 1 : 2;
        return rankA - rankB || a.product.name.localeCompare(b.product.name, "fr");
      });
  }, [query]);

  const selectedMatch = productMatches.find((item) => item.key === selectedProductKey) ?? productMatches[0];
  const selectedFamily = selectedMatch?.family;
  const sourceProduct = selectedMatch?.product;
  const guideFamilies = guideGroup === "Toutes" ? families : families.filter((family) => family.group === guideGroup);
  const sortedAlternatives = selectedFamily && sourceProduct
    ? selectedFamily.products
        .filter((product) => !(product.name === sourceProduct.name && product.lab === sourceProduct.lab))
        .sort((a, b) => {
          const priorityGap = supplyProfile(a.lab).priority - supplyProfile(b.lab).priority;
          return priorityGap || a.name.localeCompare(b.name, "fr");
        })
    : [];

  const copyFamily = async () => {
    if (!selectedFamily || !sourceProduct) return;
    const value = `Équivalents de ${sourceProduct.name}\n${sortedAlternatives.map((product) => {
      const supply = supplyProfile(product.lab);
      const variants = product.variants?.map((variant) => `\n  ${variant.size} · EAN ${variant.ean}`).join("") ?? "";
      return `${product.lab} : ${product.name} — ${supply.channel}${variants}`;
    }).join("\n")}\nÀ vérifier : dimensions, indication, remboursement et disponibilité.`;
    try {
      await navigator.clipboard.writeText(value);
      setNotice("Liste copiée");
    } catch {
      setNotice("Copie non disponible sur ce poste");
    }
    window.setTimeout(() => setNotice(""), 2200);
  };

  const chooseGuideFamily = (family: DressingFamily) => {
    const firstProduct = family.products[0];
    setQuery(firstProduct.name);
    setSelectedProductKey(`${family.id}:${firstProduct.lab}:${firstProduct.name}`);
    setShowGuide(false);
    window.setTimeout(() => searchRef.current?.focus(), 0);
  };

  return (
    <main className="dressing-module dressing-simple-module">
      <header className="module-header dressing-module-header">
        <button type="button" className="back-button" onClick={onBack}>← Accueil</button>
        <div className="module-brand"><span className="brand-mark">sp</span><div><strong>saint-laurent</strong><small>PHARMACIES</small></div></div>
        <div className="module-header-actions">
          <a className="module-tools-shortcut" href="/?tools=1" title="Ouvrir les applications"><span>🧰</span><b>Applications</b></a>
          <span className="module-status"><i />Référentiel comptoir</span>
        </div>
      </header>

      <section className="dressing-hero dressing-hero-simple">
        <p className="dressing-kicker">🩹 ÉQUIVALENCES IMMÉDIATES</p>
        <h1>Quel pansement remplace celui-ci ?</h1>
        <p>Tapez simplement son nom. Le portail affiche directement les produits comparables.</p>
      </section>

      <section className="dressing-search-card dressing-search-card-simple">
        <label className="dressing-search dressing-search-simple">
          <span>🔎</span>
          <input
            ref={searchRef}
            autoFocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedProductKey("");
            }}
            placeholder="Tapez un nom : Mepore, Urgotul, Aquacel…"
          />
          {query && <button type="button" onClick={() => { setQuery(""); setSelectedProductKey(""); }}>×</button>}
        </label>

        {!query && (
          <div className="dressing-examples">
            <span>Essayez :</span>
            {["Mepore", "Urgotul", "Aquacel Foam", "Mepilex Border", "Tegaderm"].map((item) => (
              <button type="button" key={item} onClick={() => setQuery(item)}>{item}</button>
            ))}
          </div>
        )}

        {query.trim().length === 1 && <p className="dressing-type-more">Tapez encore une lettre…</p>}

        {productMatches.length > 1 && (
          <div className="dressing-match-choices">
            <span>Précisez si besoin :</span>
            {productMatches.slice(0, 8).map(({ product, family, key }) => (
              <button
                type="button"
                key={key}
                className={selectedMatch?.key === key ? "active" : ""}
                onClick={() => setSelectedProductKey(key)}
              >
                <b>{product.name}</b>
                <small>{product.lab} · {family.label}{product.variants?.length ? ` · ${product.variants.length} tailles` : ""}</small>
              </button>
            ))}
          </div>
        )}

        {query.trim().length >= 2 && !productMatches.length && (
          <div className="dressing-no-direct-result">
            <span>?</span>
            <div><b>Produit non trouvé</b><p>Essayez un nom plus court ou utilisez la recherche par famille.</p></div>
            <button type="button" onClick={() => setShowGuide(true)}>Voir les familles</button>
          </div>
        )}
      </section>

      {selectedFamily && sourceProduct && (
        <section className="dressing-direct-result">
          <div className="dressing-recognized">
            <div className="dressing-recognized-icon">✓</div>
            <div>
              <small>Produit reconnu</small>
              <h2>{sourceProduct.name}</h2>
              <p>{sourceProduct.lab} · <b>{selectedFamily.label}</b></p>
            </div>
            <div className="dressing-recognized-meta">
              <span><small>Absorption</small><AbsorptionScale level={selectedFamily.absorption} /></span>
              <span><small>Type</small><b>{selectedFamily.adhesion}</b></span>
            </div>
            <ProductVariants product={sourceProduct} defaultOpen />
          </div>

          <div className="dressing-direct-title">
            <div>
              <p>Produits comparables</p>
              <h2>Équivalents de {sourceProduct.name}</h2>
              <span>Les marques préférées remontent automatiquement en premier.</span>
            </div>
            <button type="button" onClick={copyFamily}>⧉ Copier la liste</button>
          </div>

          <div className="dressing-supply-policy" aria-label="Ordre de préférence d’approvisionnement">
            <div>
              <small>Vos circuits d’achat</small>
              <strong>Ordre d’affichage</strong>
            </div>
            <span className="soineo"><b>1 · Soineo</b><small>Direct +++</small></span>
            <span className="marque-verte"><b>2 · Marque Verte</b><small>CERP · dépannage</small></span>
            <span className="lohmann"><b>3 · Lohmann</b><small>Direct</small></span>
            <span className="hartmann"><b>4 · Hartmann</b><small>CERP · technique</small></span>
          </div>

          <div className="dressing-direct-grid">
            {sortedAlternatives.map((product) => {
              const supply = supplyProfile(product.lab);
              return (
                <article className={supply.className} key={`${selectedFamily.id}-${product.lab}-${product.name}`}>
                  <span>🩹</span>
                  <div><small>{product.lab}</small><strong>{product.name}</strong></div>
                  <div className="dressing-supply-badge">
                    <b>{supply.label}</b>
                    <small>{supply.channel}</small>
                  </div>
                  <ProductVariants product={product} defaultOpen={supply.className === "marque-verte"} />
                </article>
              );
            })}
          </div>

          <div className="dressing-direct-vigilance">
            <span>⚠️</span>
            <div>
              <b>À vérifier avant de délivrer</b>
              <p>{selectedFamily.advice} Dimensions, forme et remboursement doivent correspondre.</p>
            </div>
          </div>
        </section>
      )}

      {!query && (
        <section className="dressing-simple-empty">
          <span>🩹</span>
          <h2>Un nom suffit</h2>
          <p>Commencez à taper le pansement présent sur l’ordonnance ou apporté par le patient.</p>
        </section>
      )}

      <section className={`dressing-guide-collapsible ${showGuide ? "open" : ""}`}>
        <button type="button" className="dressing-guide-toggle" onClick={() => setShowGuide((value) => !value)}>
          <span>Je ne connais pas le nom du produit</span>
          <b>{showGuide ? "Replier ↑" : "Chercher par famille ↓"}</b>
        </button>
        {showGuide && (
          <div className="dressing-guide-content">
            <div className="dressing-group-scroll">
              {groups.map((item) => (
                <button type="button" key={item} className={guideGroup === item ? "active" : ""} onClick={() => setGuideGroup(item)}>{item}</button>
              ))}
            </div>
            <div className="dressing-guide-family-grid">
              {guideFamilies.map((family) => (
                <article key={family.id}>
                  <span>{family.icon}</span>
                  <div><small>{family.group}</small><strong>{family.label}</strong><p>{family.description}</p></div>
                  <button type="button" onClick={() => chooseGuideFamily(family)}>Voir les équivalents</button>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>

      <p className="dressing-simple-disclaimer">
        Aide à l’identification d’une équivalence fonctionnelle. Toujours vérifier l’indication, la dimension, la forme, le remboursement et les données fabricant.
      </p>
      {notice && <div className="dressing-toast" role="status">{notice}</div>}
    </main>
  );
}
